package com.trevorwiebe.timesheet.loads.presentation

import com.trevorwiebe.timesheet.mileage.domain.model.unitNumberOrder
import com.trevorwiebe.timesheet.mileage.domain.model.EquipmentType
import com.trevorwiebe.timesheet.mileage.domain.model.Equipment
import com.trevorwiebe.timesheet.mileage.domain.EquipmentRepository
import com.trevorwiebe.timesheet.core.presentation.DisposableViewModel
import androidx.lifecycle.viewModelScope
import com.trevorwiebe.timesheet.admin.carrier.domain.CarrierRepository
import com.trevorwiebe.timesheet.admin.customer.domain.CustomerRepository
import com.trevorwiebe.timesheet.admin.loadrate.domain.LoadRateRepository
import com.trevorwiebe.timesheet.admin.loadrate.domain.model.LoadRate
import com.trevorwiebe.timesheet.calendar.domain.usecases.GetTimeOffRequests
import com.trevorwiebe.timesheet.core.domain.CoreRepository
import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.core.domain.model.Carrier
import com.trevorwiebe.timesheet.core.domain.model.Customer
import com.trevorwiebe.timesheet.core.domain.model.DatabaseUserModel
import com.trevorwiebe.timesheet.core.domain.model.TimeOffRequestModel
import com.trevorwiebe.timesheet.loads.data.LoadFileStorageRepository
import com.trevorwiebe.timesheet.loads.domain.LoadsRepository
import com.trevorwiebe.timesheet.loads.domain.model.Load
import com.trevorwiebe.timesheet.loads.domain.jpegsToPdf
import com.trevorwiebe.timesheet.loads.domain.model.LoadAttachment
import com.trevorwiebe.timesheet.loads.domain.model.LoadStop
import com.trevorwiebe.timesheet.loads.domain.model.StopType
import com.trevorwiebe.timesheet.loads.domain.model.drops
import com.trevorwiebe.timesheet.loads.domain.model.isScheduled
import com.trevorwiebe.timesheet.loads.domain.model.pickups
import com.trevorwiebe.timesheet.loads.domain.usecases.AddLoad
import com.trevorwiebe.timesheet.loads.domain.usecases.DeleteLoad
import com.trevorwiebe.timesheet.loads.domain.usecases.LoadStopDraft
import com.trevorwiebe.timesheet.loads.domain.usecases.MoveLoad
import com.trevorwiebe.timesheet.loads.domain.usecases.UpdateLoad
import com.trevorwiebe.timesheet.loads.domain.usecases.UpdateLoadAssignment
import com.trevorwiebe.timesheet.loads.presentation.admin.AddLoadFormState
import com.trevorwiebe.timesheet.loads.presentation.admin.DuplicateLoadFormState
import com.trevorwiebe.timesheet.loads.presentation.admin.LoadRateFormEntry
import com.trevorwiebe.timesheet.loads.presentation.admin.LoadStopEntry
import com.trevorwiebe.timesheet.loads.presentation.admin.LoadsEvent
import com.trevorwiebe.timesheet.loads.presentation.admin.LoadsFilter
import com.trevorwiebe.timesheet.loads.presentation.admin.LoadsState
import com.trevorwiebe.timesheet.loads.presentation.admin.todayDate
import com.trevorwiebe.timesheet.loads.presentation.driver.DriverLoadCardUi
import com.trevorwiebe.timesheet.loads.presentation.driver.DriverLoadGroup
import com.trevorwiebe.timesheet.loads.presentation.driver.DriverLoadsEvent
import com.trevorwiebe.timesheet.loads.presentation.driver.DriverLoadsState
import com.trevorwiebe.timesheet.loads.presentation.driver.DriverStopUi
import com.trevorwiebe.timesheet.loads.presentation.driver.MapsLink
import com.trevorwiebe.timesheet.mileage.data.ReceiptStorageRepository
import com.trevorwiebe.timesheet.mileage.domain.LocationRepository
import com.trevorwiebe.timesheet.mileage.domain.model.Location
import com.trevorwiebe.timesheet.mileage.domain.model.LocationType
import com.trevorwiebe.timesheet.mileage.domain.model.cityState
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate

class LoadsViewModel(
    private val coreRepository: CoreRepository,
    private val loadsRepository: LoadsRepository,
    private val customerRepository: CustomerRepository,
    private val carrierRepository: CarrierRepository,
    private val locationRepository: LocationRepository,
    private val loadRateRepository: LoadRateRepository,
    private val addLoad: AddLoad,
    private val updateLoad: UpdateLoad,
    private val moveLoad: MoveLoad,
    private val updateLoadAssignment: UpdateLoadAssignment,
    private val deleteLoad: DeleteLoad,
    private val getTimeOffRequests: GetTimeOffRequests,
    private val loadFileStorageRepository: LoadFileStorageRepository,
    private val receiptStorageRepository: ReceiptStorageRepository,
    private val equipmentRepository: EquipmentRepository,
) : DisposableViewModel() {

    private val _state = MutableStateFlow(LoadsState(today = todayDate()))
    val state = _state.asStateFlow()

    private val _driverState = MutableStateFlow(DriverLoadsState())
    val driverState = _driverState.asStateFlow()

    private var driverLoadsJob: Job? = null

    private var driverLoads: List<Load> = emptyList()

    private fun updateState(transform: (LoadsState) -> LoadsState) {
        _state.update { current ->
            val next = transform(current)
            next.copy(suggestedLoadRates = computeSuggestedLoadRates(next))
        }
    }

    private fun computeSuggestedLoadRates(state: LoadsState): List<LoadRate> {
        val pair = state.addLoadForm.stopPair ?: return emptyList()
        return state.loadRates.filter {
            it.pickupLocationId == pair.first && it.dropLocationId == pair.second
        }
    }

    init {
        start()
    }

    fun onDriverEvent(event: DriverLoadsEvent) {
        when (event) {
            is DriverLoadsEvent.OnRetry ->
                _state.value.currentUser?.let { loadDriverLoads(it.id) }

            is DriverLoadsEvent.OnOpenDocument -> loadDocumentPdf(event.documentUrl)

            is DriverLoadsEvent.OnScanDocument -> changeDriverDocuments(event.loadId) {
                val userId = _state.value.currentUser?.id ?: return@changeDriverDocuments null
                val attachments = driverLoads.find { it.id == event.loadId }?.attachments.orEmpty()
                val scan = buildScan(nextScanName(attachments), userId, event.pages)
                scan?.let { { current -> current + it } }
            }

            is DriverLoadsEvent.OnDeleteDocument -> changeDriverDocuments(event.loadId) {
                { current -> current.filterNot { it.url == event.documentUrl } }
            }
        }
    }

    private fun loadDocumentPdf(url: String) {
        if (url in _state.value.documentPdfs) return
        viewModelScope.launch {
            // Empty bytes fail to render, so the viewer shows an error instead of spinning.
            cacheDocumentPdf(url, receiptStorageRepository.downloadBytes(url) ?: ByteArray(0))
        }
    }

    private fun cacheDocumentPdf(url: String, pdf: ByteArray) {
        updateState { it.copy(documentPdfs = it.documentPdfs + (url to pdf)) }
    }

    /**
     * [prepare] does the slow work (uploads) and returns how to change the load's attachments, or
     * null on failure. The change is applied to the load's latest attachments, read after the
     * uploads finish, and only the attachments field is written.
     */
    private fun changeDriverDocuments(
        loadId: String,
        prepare: suspend () -> ((List<LoadAttachment>) -> List<LoadAttachment>)?,
    ) {
        _driverState.update { it.copy(documentLoadId = loadId, isSavingDocument = true, documentError = null) }
        viewModelScope.launch {
            val change = prepare()
            val load = driverLoads.find { it.id == loadId }
            val error = if (change == null || load == null) {
                SCAN_FAILED
            } else {
                loadsRepository.updateAttachments(loadId, change(load.attachments)).error
            }
            _driverState.update { it.copy(isSavingDocument = false, documentError = error) }
        }
    }

    private fun nextScanName(attachments: List<LoadAttachment>): String {
        val names = attachments.mapTo(mutableSetOf()) { it.name }
        return generateSequence(1) { it + 1 }.map { "Scan $it.pdf" }.first { it !in names }
    }

    /** Uploads a PDF of the scanned [pages]. Returns null if the PDF build or upload fails. */
    private suspend fun buildScan(name: String, uploadedById: String, pages: List<ByteArray>): LoadAttachment? {
        val orgId = _state.value.currentUser?.organizationId ?: return null
        val pdf = runCatching { jpegsToPdf(pages) }.getOrNull() ?: return null
        val url = loadFileStorageRepository.uploadLoadFile(pdf, orgId, name).data as? String ?: return null
        cacheDocumentPdf(url, pdf)
        return LoadAttachment(name = name, url = url, uploadedById = uploadedById)
    }

    fun onEvent(event: LoadsEvent) {
        when (event) {
            is LoadsEvent.OnRetryLoadUser -> start()

            // Filters: update the filter only; the board reads state.filteredLoads.
            is LoadsEvent.OnFilterByCustomer ->
                updateState { it.copy(filter = it.filter.copy(customerId = event.customerId)) }

            is LoadsEvent.OnFilterByEmployee ->
                updateState { it.copy(filter = it.filter.copy(employeeId = event.employeeId)) }

            is LoadsEvent.OnClearFilters ->
                updateState { it.copy(filter = LoadsFilter()) }

            is LoadsEvent.OnFilterByCarrier ->
                updateState { it.copy(filter = it.filter.copy(carrierId = event.carrierId)) }

            is LoadsEvent.OnFilterByPickupLocation ->
                updateState { it.copy(filter = it.filter.copy(pickupLocationId = event.locationId)) }

            is LoadsEvent.OnFilterByDropLocation ->
                updateState { it.copy(filter = it.filter.copy(dropLocationId = event.locationId)) }

            // Completed screen date filters: pure filter updates, the list is derived from state.
            is LoadsEvent.OnStartDateClick ->
                updateState { it.copy(filter = it.filter.copy(showStartPicker = true)) }

            is LoadsEvent.OnEndDateClick ->
                updateState { it.copy(filter = it.filter.copy(showEndPicker = true)) }

            is LoadsEvent.OnStartDateSelected ->
                updateState {
                    it.copy(filter = it.filter.copy(startDate = event.date, showStartPicker = false))
                }

            is LoadsEvent.OnEndDateSelected ->
                updateState {
                    it.copy(filter = it.filter.copy(endDate = event.date, showEndPicker = false))
                }

            is LoadsEvent.OnDismissStartPicker ->
                updateState { it.copy(filter = it.filter.copy(showStartPicker = false)) }

            is LoadsEvent.OnDismissEndPicker ->
                updateState { it.copy(filter = it.filter.copy(showEndPicker = false)) }

            is LoadsEvent.OnShowAddDialog ->
                updateState {
                    it.copy(
                        showAddDialog = true,
                        addLoadForm = AddLoadFormState(),
                        editingLoad = null,
                    )
                }

            is LoadsEvent.OnDismissAddDialog ->
                updateState {
                    it.copy(
                        showAddDialog = false,
                        isSaving = false,
                        editingLoad = null,
                        showDeleteConfirm = false,
                        isDeleting = false,
                        showDuplicateDialog = false,
                        duplicateForm = DuplicateLoadFormState(),
                        isDuplicating = false,
                    )
                }

            is LoadsEvent.OnEditLoad -> updateState {
                it.copy(
                    showAddDialog = true,
                    editingLoad = event.load,
                    addLoadForm = formFromLoad(event.load),
                )
            }

            is LoadsEvent.OnRequestDelete -> updateState { it.copy(showDeleteConfirm = true) }
            is LoadsEvent.OnDismissDelete -> updateState { it.copy(showDeleteConfirm = false) }
            is LoadsEvent.OnConfirmDelete -> deleteLoad()

            is LoadsEvent.OnShowDuplicateDialog ->
                updateState {
                    it.copy(
                        showDuplicateDialog = true,
                        duplicateForm = DuplicateLoadFormState(
                            // Prefilled from the source load; the user keeps or edits the amounts.
                            rate = it.editingLoad?.rate?.let { r -> LoadRateFormEntry.fromRateInfo(r) },
                        ),
                    )
                }

            is LoadsEvent.OnDismissDuplicateDialog ->
                updateState {
                    it.copy(showDuplicateDialog = false, duplicateForm = DuplicateLoadFormState(), isDuplicating = false)
                }

            is LoadsEvent.OnUpdateDuplicateForm ->
                updateState { it.copy(duplicateForm = event.form) }

            is LoadsEvent.OnSaveDuplicate -> saveDuplicate()

            is LoadsEvent.OnUpdateForm ->
                updateState { it.copy(addLoadForm = it.autoFillRate(event.form, previous = it.addLoadForm)) }

            is LoadsEvent.OnAddPickup -> updateState {
                val form = it.addLoadForm
                it.copy(addLoadForm = form.copy(pickups = form.pickups + LoadStopEntry()))
            }

            is LoadsEvent.OnAddDrop -> updateState {
                val form = it.addLoadForm
                it.copy(addLoadForm = form.copy(drops = form.drops + LoadStopEntry()))
            }

            is LoadsEvent.OnRemovePickup -> updateState {
                val form = it.addLoadForm
                if (form.pickups.size <= 1) return@updateState it
                val updated = form.copy(pickups = form.pickups.filterIndexed { i, _ -> i != event.index })
                it.copy(addLoadForm = it.autoFillRate(updated, previous = form))
            }

            is LoadsEvent.OnRemoveDrop -> updateState {
                val form = it.addLoadForm
                if (form.drops.size <= 1) return@updateState it
                val updated = form.copy(drops = form.drops.filterIndexed { i, _ -> i != event.index })
                it.copy(addLoadForm = it.autoFillRate(updated, previous = form))
            }

            is LoadsEvent.OnSaveLoad -> saveLoad()

            is LoadsEvent.OnAttachmentSelected -> uploadAttachment(event.fileName, event.bytes)

            is LoadsEvent.OnScanDocument -> changeFormScans {
                val userId = _state.value.currentUser?.id ?: return@changeFormScans null
                val name = nextScanName(_state.value.addLoadForm.attachments)
                buildScan(name, userId, event.pages)?.let { { current -> current + it } }
            }

            is LoadsEvent.OnOpenDocument -> loadDocumentPdf(event.documentUrl)

            is LoadsEvent.OnRemoveAttachment -> updateState {
                val form = it.addLoadForm
                it.copy(
                    addLoadForm = form.copy(
                        attachments = form.attachments.filterIndexed { i, _ -> i != event.index },
                    )
                )
            }

            is LoadsEvent.OnSelectLoadRate -> updateState {
                val form = it.addLoadForm
                val newRate = event.loadRate
                    ?.let { template -> resnapshot(form.rate, template) }
                    ?.copy(autoFilled = false)
                it.copy(addLoadForm = form.copy(rate = newRate))
            }

            is LoadsEvent.OnUncompleteLoad -> uncompleteLoad(event.load)

            is LoadsEvent.OnMoveLoad -> moveLoad(event.load, event.scheduledDate, event.employeeId, event.carrierId)

            is LoadsEvent.OnToggleDispatchConfirmed -> toggleDispatchConfirmed(event.load)
        }
    }

    private fun moveLoad(load: Load, scheduledDate: LocalDate?, employeeId: String, carrierId: String = "") {
        val targetCarrier = if (scheduledDate == null) "" else carrierId
        val targetEmployee = if (scheduledDate == null || targetCarrier.isNotEmpty()) "" else employeeId
        if (load.scheduledDate == scheduledDate &&
            load.employeeId == targetEmployee &&
            load.carrierId == targetCarrier
        ) return
        viewModelScope.launch {
            // The Firestore listener refreshes the list on success; only surface failures here.
            val result = moveLoad.invoke(
                load = load,
                scheduledDate = scheduledDate,
                employeeId = targetEmployee,
                carrierId = targetCarrier,
            )
            if (!result.error.isNullOrEmpty()) {
                updateState { it.copy(error = result.error) }
            }
        }
    }

    private fun uncompleteLoad(load: Load) {
        viewModelScope.launch {
            val result = moveLoad.invoke(load = load, scheduledDate = null, employeeId = "")
            if (result.error.isNullOrEmpty()) {
                updateState {
                    it.copy(
                        showAddDialog = false,
                        addLoadForm = AddLoadFormState(),
                        editingLoad = null,
                    )
                }
            } else {
                updateState { it.copy(error = result.error) }
            }
        }
    }

    private fun toggleDispatchConfirmed(load: Load) {
        viewModelScope.launch {
            // The Firestore listener refreshes the list on success; only surface failures here.
            val result = updateLoadAssignment.invoke(
                load = load,
                employeeId = load.employeeId,
                dispatchConfirmed = !load.dispatchConfirmed,
            )
            if (!result.error.isNullOrEmpty()) {
                updateState { it.copy(error = result.error) }
            }
        }
    }

    private fun uploadAttachment(fileName: String, bytes: ByteArray) {
        val orgId = _state.value.currentUser?.organizationId
        if (orgId.isNullOrEmpty()) return
        if (!fileName.endsWith(".pdf", ignoreCase = true)) {
            updateState {
                it.copy(addLoadForm = it.addLoadForm.copy(attachmentError = "Only PDF files can be attached"))
            }
            return
        }
        if (bytes.size > MAX_ATTACHMENT_BYTES) {
            updateState {
                it.copy(addLoadForm = it.addLoadForm.copy(attachmentError = "File is too large (max 20 MB)"))
            }
            return
        }
        updateState {
            it.copy(addLoadForm = it.addLoadForm.copy(isUploadingAttachment = true, attachmentError = null))
        }
        viewModelScope.launch {
            val result = loadFileStorageRepository.uploadLoadFile(bytes, orgId, fileName)
            val url = result.data as? String
            updateState {
                val form = it.addLoadForm
                it.copy(
                    addLoadForm = if (url != null) {
                        form.copy(
                            attachments = form.attachments + LoadAttachment(name = fileName, url = url),
                            isUploadingAttachment = false,
                        )
                    } else {
                        form.copy(
                            isUploadingAttachment = false,
                            attachmentError = result.error ?: "Failed to upload file",
                        )
                    }
                )
            }
        }
    }

    /** The load form's counterpart of [changeDriverDocuments]; the change is kept until the form is saved. */
    private fun changeFormScans(prepare: suspend () -> ((List<LoadAttachment>) -> List<LoadAttachment>)?) {
        updateState {
            it.copy(addLoadForm = it.addLoadForm.copy(isUploadingAttachment = true, attachmentError = null))
        }
        viewModelScope.launch {
            val change = prepare()
            updateState {
                val form = it.addLoadForm
                it.copy(
                    addLoadForm = form.copy(
                        attachments = change?.invoke(form.attachments) ?: form.attachments,
                        isUploadingAttachment = false,
                        attachmentError = if (change == null) SCAN_FAILED else null,
                    )
                )
            }
        }
    }

    private fun saveLoad() {
        val form = _state.value.addLoadForm

       val editingLoad = _state.value.editingLoad
        val driverRequired = form.isPast(_state.value.today) && !form.hasDriver &&
            (editingLoad == null || editingLoad.isScheduled)

        if (!form.hasCustomer || !form.hasAllPickupLocations || !form.hasAllDropLocations || driverRequired) {
            updateState {
                it.copy(
                    addLoadForm = form.copy(
                        customerError = if (!form.hasCustomer) "Select a customer" else null,
                        driverError = if (driverRequired) "Select the driver who ran this load" else null,
                        pickups = form.pickups.map { stop ->
                            stop.copy(locationError = if (!stop.hasLocation) "Select a location" else null)
                        },
                        drops = form.drops.map { stop ->
                            stop.copy(locationError = if (!stop.hasLocation) "Select a location" else null)
                        },
                    )
                )
            }
            return
        }

        val newCustomer = if (form.isNewCustomer) {
            Customer(
                id = "",
                contactName = form.newCustomerContactName.trim(),
                companyName = form.newCustomerCompanyName.trim(),
                address = form.newCustomerAddress.trim(),
                phone = form.newCustomerPhone.trim(),
                emails = listOf(form.newCustomerEmail.trim()).filter { it.isNotEmpty() },
                paymentTerms = form.newCustomerPaymentTerms.trim(),
                notes = form.newCustomerNotes.trim(),
            )
        } else null

        val customerId = if (form.isNewCustomer) "" else form.customerId

        fun draftFor(entry: LoadStopEntry, stopType: StopType): LoadStopDraft {
            val newLocation = if (entry.isNewLocation) {
                Location(
                    id = "",
                    name = entry.newLocationName.trim(),
                    city = entry.newLocationCity.trim(),
                    state = entry.newLocationState.trim(),
                    type = LocationType.DELIVERY,
                )
            } else null
            val stop = LoadStop(
                locationId = if (entry.isNewLocation) "" else entry.locationId,
                type = stopType,
            )
            return LoadStopDraft(stop = stop, newLocation = newLocation)
        }

        val stops = form.pickups.map { draftFor(it, StopType.PICKUP) } +
            form.drops.map { draftFor(it, StopType.DROP) }

        val load = Load(
            id = editingLoad?.id ?: form.draftId,
            customerId = customerId,
            loadNumber = form.loadNumber.trim(),
            po = form.po.trim(),
            orderNumber = form.orderNumber.trim(),
            commodity = form.commodity.trim(),
            customerRequirements = form.customerRequirements.trim(),
            pickupDate = form.pickupDate,
            originalDate = form.originalDate,
            scheduledDate = if (editingLoad != null) {
                editingLoad.scheduledDate
            } else {
                form.originalDate.takeIf { form.isPast(_state.value.today) }
            },
            stops = emptyList(), // resolved by the use case from the drafts
            // The driver comes from the form (optional, or required for after-the-fact loads).
            employeeId = form.employeeId,
            truckId = form.truckId,
            // Carrier survives an edit (the form has no carrier field), but assigning a driver
            // un-carriers the load — driver and carrier are mutually exclusive.
            carrierId = if (form.employeeId.isNotEmpty()) "" else (editingLoad?.carrierId ?: ""),
            // Preserve confirmation on edit; a new after-the-fact load with a driver is already done.
            dispatchConfirmed = editingLoad?.dispatchConfirmed
                ?: (form.isPast(_state.value.today) && form.hasDriver),
            setUp = form.setUp,
            ready = form.ready,
            rate = form.rate?.toRateInfo(),
            attachments = form.attachments,
        )

        updateState { it.copy(isSaving = true) }
        viewModelScope.launch {
            val result = if (editingLoad != null) {
                updateLoad.invoke(load = load, stopDrafts = stops, newCustomer = newCustomer)
            } else {
                addLoad.invoke(load = load, stopDrafts = stops, newCustomer = newCustomer)
            }
            if (result.error.isNullOrEmpty()) {
                updateState {
                    it.copy(
                        isSaving = false,
                        showAddDialog = false,
                        addLoadForm = AddLoadFormState(),
                        editingLoad = null,
                    )
                }
            } else {
                updateState { it.copy(isSaving = false, error = result.error) }
            }
        }
    }

    /**
     * Creates a copy of the load being edited with the new dates and load/PO/order numbers from the
     * duplicate form. Stops, commodity, customer requirements, and the customer copy verbatim; the new
     * load starts unscheduled, unassigned, and with both status flags cleared.
     */
    private fun saveDuplicate() {
        val source = _state.value.editingLoad ?: return
        val form = _state.value.duplicateForm

        val stops = source.stops.map { stop ->
            LoadStopDraft(stop = stop, newLocation = null)
        }

        val load = Load(
            id = form.draftId,
            customerId = source.customerId,
            loadNumber = form.loadNumber.trim(),
            po = form.po.trim(),
            orderNumber = form.orderNumber.trim(),
            commodity = source.commodity,
            customerRequirements = source.customerRequirements,
            pickupDate = form.pickupDate,
            originalDate = form.originalDate,
            scheduledDate = null,
            stops = emptyList(), // resolved by the use case from the drafts
            employeeId = "",
            dispatchConfirmed = false,
            setUp = false,
            ready = false,
            // The rate template carried over from the source; amounts came from the form.
            rate = form.rate?.toRateInfo(),
        )

        updateState { it.copy(isDuplicating = true) }
        viewModelScope.launch {
            val result = addLoad.invoke(load = load, stopDrafts = stops, newCustomer = null)
            if (result.error.isNullOrEmpty()) {
                updateState {
                    it.copy(
                        isDuplicating = false,
                        showDuplicateDialog = false,
                        duplicateForm = DuplicateLoadFormState(),
                        showAddDialog = false,
                        addLoadForm = AddLoadFormState(),
                        editingLoad = null,
                    )
                }
            } else {
                updateState { it.copy(isDuplicating = false, error = result.error) }
            }
        }
    }

    private fun deleteLoad() {
        val id = _state.value.editingLoad?.id
        if (id.isNullOrEmpty()) {
            updateState { it.copy(showDeleteConfirm = false) }
            return
        }
        updateState { it.copy(isDeleting = true) }
        viewModelScope.launch {
            val result = deleteLoad.invoke(id)
            if (result.error.isNullOrEmpty()) {
                updateState {
                    it.copy(
                        isDeleting = false,
                        showDeleteConfirm = false,
                        showAddDialog = false,
                        addLoadForm = AddLoadFormState(),
                        editingLoad = null,
                    )
                }
            } else {
                updateState {
                    it.copy(isDeleting = false, showDeleteConfirm = false, error = result.error)
                }
            }
        }
    }

    /**
     * Reverses [saveLoad]'s draft-building to seed the form from an existing load: the stops become
     * [LoadStopEntry]s (existing locations, never new). Empty pickup/drop lists fall back to one
     * blank entry so the form stays valid.
     */
    private fun formFromLoad(load: Load): AddLoadFormState {
        fun entryOf(stop: LoadStop) = LoadStopEntry(locationId = stop.locationId)

        return AddLoadFormState(
            customerId = load.customerId,
            pickupDate = load.pickupDate,
            originalDate = load.originalDate,
            employeeId = load.employeeId,
            truckId = load.truckId,
            pickups = load.pickups.map { entryOf(it) }.ifEmpty { listOf(LoadStopEntry()) },
            drops = load.drops.map { entryOf(it) }.ifEmpty { listOf(LoadStopEntry()) },
            loadNumber = load.loadNumber,
            po = load.po,
            orderNumber = load.orderNumber,
            commodity = load.commodity,
            customerRequirements = load.customerRequirements,
            setUp = load.setUp,
            ready = load.ready,
            // Seeded regardless of role so a non-admin editing a rated load preserves it on save.
            rate = load.rate?.let { LoadRateFormEntry.fromRateInfo(it) },
            attachments = load.attachments,
        )
    }

    private fun loadLoads() {
        viewModelScope.launch {
            loadsRepository.queryLoads().collect { result ->
                @Suppress("UNCHECKED_CAST")
                val loads = result.data as? List<Load> ?: emptyList()
                updateState { it.copy(loads = loads, isLoading = false, error = result.error) }
            }
        }
    }

    private fun loadCustomers() {
        viewModelScope.launch {
            customerRepository.getCustomers().collect { result ->
                @Suppress("UNCHECKED_CAST")
                val customers = (result.data as? List<Customer> ?: emptyList()).filter { !it.deleted }
                updateState { it.copy(customers = customers) }
            }
        }
    }

    private fun loadCarriers() {
        viewModelScope.launch {
            carrierRepository.getCarriers().collect { result ->
                @Suppress("UNCHECKED_CAST")
                val carriers = result.data as? List<Carrier> ?: emptyList()
                updateState { it.copy(carriers = carriers) }
            }
        }
    }

    private fun loadLocations() {
        viewModelScope.launch {
            locationRepository.getLocations().collect { result ->
                @Suppress("UNCHECKED_CAST")
                val locations = (result.data as? List<Location> ?: emptyList())
                    .filter { !it.deleted && it.type == LocationType.DELIVERY }
                updateState { it.copy(locations = locations) }
            }
        }
    }

    private fun loadTrucks() {
        viewModelScope.launch {
            equipmentRepository.getEquipment().collect { result ->
                @Suppress("UNCHECKED_CAST")
                val trucks = (result.data as? List<Equipment> ?: emptyList())
                    .filter { it.type == EquipmentType.TRUCK && !it.deleted }
                    .sortedWith(compareBy(unitNumberOrder) { it.unitNumber })
                updateState { it.copy(trucks = trucks) }
            }
        }
    }

    private fun loadLoadRates() {
        viewModelScope.launch {
            loadRateRepository.getLoadRates().collect { result ->
                @Suppress("UNCHECKED_CAST")
                val loadRates = result.data as? List<LoadRate> ?: emptyList()
                updateState { it.copy(loadRates = loadRates) }
            }
        }
    }

    /**
     * Re-snapshot the pricing fields from [template] but keep whatever amounts were already
     * typed, except when the template carries a preset price — that overwrites the amount.
     */
    private fun resnapshot(current: LoadRateFormEntry?, template: LoadRate): LoadRateFormEntry {
        val snapshot = LoadRateFormEntry.fromTemplate(template)
        return current?.copy(
            parentRateId = snapshot.parentRateId,
            rateCategory = snapshot.rateCategory,
            rateType = snapshot.rateType,
            unit = snapshot.unit,
            perUnitValue = if (template.perUnit != 0) snapshot.perUnitValue else current.perUnitValue,
            flatRateValue = if (template.flatRate != 0) snapshot.flatRateValue else current.flatRateValue,
            pickupLocationId = snapshot.pickupLocationId,
            dropLocationId = snapshot.dropLocationId,
        )?.reconciledWeightFields() ?: snapshot
    }

    /**
     * Applies the stop-pair rate auto-fill to [form]. Runs only when the first-pickup/first-drop
     * pair changed from [previous], and never touches a rate the user picked manually. A single
     * matching template is applied; otherwise a previously auto-filled rate is cleared.
     */
    private fun LoadsState.autoFillRate(
        form: AddLoadFormState,
        previous: AddLoadFormState,
    ): AddLoadFormState {
        if (form.stopPair == previous.stopPair) return form
        val rate = form.rate
        if (rate != null && !rate.autoFilled) return form
        val matches = form.stopPair?.let { (pickup, drop) ->
            loadRates.filter { it.pickupLocationId == pickup && it.dropLocationId == drop }
        }.orEmpty()
        val single = matches.singleOrNull()
        return when {
            single != null -> form.copy(rate = resnapshot(rate, single).copy(autoFilled = true))
            rate != null -> form.copy(rate = null)
            else -> form
        }
    }

    private fun loadUsers() {
        viewModelScope.launch {
            val result = coreRepository.getUserList()
            if (result.error.isNullOrEmpty()) {
                @Suppress("UNCHECKED_CAST")
                val users = (result.data as? List<DatabaseUserModel> ?: emptyList())
                    .filter { !it.deleted }
                    .sortedBy { it.displayName.lowercase() }
                updateState { it.copy(users = users) }
            }
        }
    }

    private fun loadTimeOffRequests() {
        viewModelScope.launch {
            getTimeOffRequests().collect { result ->
                @Suppress("UNCHECKED_CAST")
                val approved = (result.data as? List<TimeOffRequestModel> ?: emptyList())
                    .filter { it.approved == true }
                updateState { it.copy(approvedTimeOff = approved) }
            }
        }
    }

    /**
     * Resolve the user first, then subscribe only to the data their role actually renders —
     * a driver never opens the org-wide dispatch feeds.
     */
    private fun start() {
        viewModelScope.launch {
            updateState { it.copy(userLoading = true, userLoadFailed = false) }

            val user = coreRepository.getDatabaseUser().data as? DatabaseUserModel
            if (user == null) {
                updateState { it.copy(userLoading = false, userLoadFailed = true) }
                return@launch
            }
            updateState {
                it.copy(currentUser = user, userLoading = false, userLoadFailed = false)
            }

            if (_state.value.isElevated) loadDispatchBoard() else loadDriverLoads(user.id)
        }
    }

    private fun loadDispatchBoard() {
        loadLoads()
        loadCustomers()
        loadCarriers()
        loadLocations()
        loadTrucks()
        loadLoadRates()
        loadUsers()
        loadTimeOffRequests()
    }

    // Retry re-enters this while the failed collector is still live, so drop the old one first
    // or every tap leaves another Firestore subscription writing to _driverState.
    private fun loadDriverLoads(userId: String) {
        driverLoadsJob?.cancel()
        driverLoadsJob = viewModelScope.launch {
            _driverState.update { it.copy(isLoading = true, error = null) }

            // Unlike the dispatch board, driver locations keep deleted entries — they are
            // display lookups only, and old loads must still resolve their names.
            combine(
                loadsRepository.queryDispatchedLoadsByDriverId(userId),
                locationRepository.getLocations(),
            ) { loadsResult, locationsResult -> loadsResult to locationsResult }
                .collect { (loadsResult, locationsResult) ->
                    @Suppress("UNCHECKED_CAST")
                    val loads = loadsResult.data as? List<Load> ?: emptyList()
                    @Suppress("UNCHECKED_CAST")
                    val locationsById = (locationsResult.data as? List<Location> ?: emptyList())
                        .associateBy { it.id }

                    driverLoads = loads
                    _driverState.update {
                        it.copy(
                            groupedLoads = groupDriverLoads(loads, locationsById, userId),
                            isLoading = false,
                            error = loadsResult.error ?: locationsResult.error,
                        )
                    }
                }
        }
    }

    /**
     * The date sections newest first, with a defensive "Unscheduled" bucket last for
     * loads without a scheduled date.
     */
    private fun groupDriverLoads(
        loads: List<Load>,
        locationsById: Map<String, Location>,
        userId: String,
    ): List<DriverLoadGroup> {
        val byDate = loads.groupBy { it.scheduledDate }
        val groups = byDate.keys.filterNotNull().sortedDescending().map { date ->
            DriverLoadGroup(
                date = date,
                label = Util.toDayOfWeekMonthDay(date),
                loads = byDate.getValue(date)
                    .sortedBy { it.loadNumber }
                    .map { toDriverCardUi(it, locationsById, userId) },
            )
        }
        val unscheduled = byDate[null] ?: return groups
        return groups + DriverLoadGroup(
            date = null,
            label = "Unscheduled",
            loads = unscheduled.map { toDriverCardUi(it, locationsById, userId) },
        )
    }

    private fun toDriverCardUi(
        load: Load,
        locationsById: Map<String, Location>,
        userId: String,
    ) = DriverLoadCardUi(
        id = load.id,
        loadNumber = load.loadNumber,
        orderNumber = load.orderNumber,
        po = load.po,
        commodity = load.commodity,
        customerRequirements = load.customerRequirements,
        pickups = load.pickups.map { toDriverStopUi(it, locationsById) },
        drops = load.drops.map { toDriverStopUi(it, locationsById) },
        documents = load.attachments.filter { it.uploadedById == userId },
    )

    private fun toDriverStopUi(
        stop: LoadStop,
        locationsById: Map<String, Location>,
    ): DriverStopUi {
        val location = locationsById[stop.locationId]
        return DriverStopUi(
            name = location?.name ?: "—",
            address = location?.address?.trim() ?: "",
            cityState = location?.cityState ?: "",
            mapsLink = location?.let { mapsLinkFor(it) },
        )
    }

    /**
     * Maps link for a stop, or null when the location has neither a pin nor a street address
     * (name/city alone isn't precise enough to navigate to). Each platform builds its own
     * maps URL from this so the driver sees the location's NAME as the destination while the
     * app navigates to the exact pin underneath.
     */
    private companion object {
        const val MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024
        const val SCAN_FAILED = "Couldn't save the scan. Check your connection and try again."
    }

    private fun mapsLinkFor(location: Location): MapsLink? {
        val lat = location.latitude
        val lng = location.longitude
        val searchText = listOf(location.name, location.address, location.city, location.state)
            .filter { it.isNotBlank() }
            .joinToString(", ")
        return when {
            lat != null && lng != null -> MapsLink(
                latLng = "$lat,$lng",
                encodedLabel = Util.percentEncode(location.name.trim()),
                encodedQuery = Util.percentEncode(searchText),
            )

            location.address.isNotBlank() -> MapsLink(
                latLng = null,
                encodedLabel = Util.percentEncode(location.name.trim()),
                encodedQuery = Util.percentEncode(searchText),
            )

            else -> null
        }
    }
}
